import { PostStatsBreakdownEntry, StatsResponse } from "@/services/bedrock";
import { Button, Table, Badge } from "@/components/ui";
import * as React from "react";

import "./styles.scss";
import { exportPostsStatsToCsv, printDateWithTimeZone, URLS, kFormatter } from "@/utils";
import { SocialNetworkIcon } from "@/components/SocialNetworkIcon";
import { get, orderBy } from "lodash";
import { Eye, Heart, MessageCircle, Share2, FileDown, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatCard } from "@/components/PostsStats/components";

interface PostsStatsProps {
  stats: StatsResponse | undefined;
  smartLinkClicks?: number;
  showSmartLinkStats?: () => void;
  type: "Prompt" | "Campaign" | "Design";
  loading: boolean;
  /** Makes the breakdown rows that name a post clickable. */
  onRowClick?: (entry: PostStatsBreakdownEntry) => void;
}

type Props = PostsStatsProps;
type SortDirection = "asc" | "desc";

// Counts read best highest first; dates and names start ascending.
const DESC_FIRST_COLUMNS = new Set([
  "postedTimestamp",
  "latestStats.impressionCount",
  "latestStats.totalReactions",
  "latestStats.commentCount",
  "latestStats.shareCount",
]);

const PostsStats: React.FC<Props> = ({ stats, smartLinkClicks, showSmartLinkStats, type, loading, onRowClick }) => {
  const [sortConfig, setSortConfig] = React.useState<{
    column: string | null;
    direction: SortDirection;
  }>({ column: "postedTimestamp", direction: "desc" });

  const sortedBreakdown = React.useMemo(() => {
    if (!stats?.breakdown) {
      return [];
    }
    if (sortConfig.column) {
      const column = sortConfig.column;
      // Rows with no value ("--") stay at the bottom in both directions; lodash puts them first when descending.
      return orderBy(stats.breakdown, [entry => get(entry, column) == null, column], ["asc", sortConfig.direction]);
    }
    return stats.breakdown;
  }, [stats?.breakdown, sortConfig.column, sortConfig.direction]);

  const requestSort = (column: string) => {
    let direction: SortDirection = DESC_FIRST_COLUMNS.has(column) ? "desc" : "asc";
    if (sortConfig.column === column) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ column, direction });
  };

  const exportStats = () => {
    if (stats) {
      if (type === "Design") {
        const designId = "designPackageId" in stats ? stats.designPackageId : "";
        exportPostsStatsToCsv(`design-${designId}-posts`, stats);
        return;
      }
      const id = "promptId" in stats ? stats.promptId : "campaignId" in stats ? stats.campaignId : "";

      exportPostsStatsToCsv(`SocialCoach${type}Stats${id}`, stats);
    }
  };

  const wrapInLinkIfHrefIsPresent = (href: string | undefined, children: React.ReactNode): React.ReactNode => {
    if (!href) return children;
    return (
      <a className="externalLink" href={href} onClick={e => e.stopPropagation()} target="_blank">
        {children}
      </a>
    );
  };

  const SortableHeader = ({
    column,
    title,
    align = "left",
  }: {
    column: string;
    title: string;
    align?: "left" | "center" | "right";
  }) => {
    const isActive = sortConfig.column === column;
    return (
      <Table.Head
        className={cn(
          "cursor-pointer hover:bg-slate-50 transition-colors whitespace-nowrap py-3",
          align === "center" && "text-center",
          align === "right" && "text-right"
        )}
        onClick={() => requestSort(column)}
      >
        <div
          className={cn(
            "flex items-center gap-2 font-bold text-slate-900 uppercase text-xs tracking-wider",
            align === "center" && "justify-center",
            align === "right" && "justify-end"
          )}
        >
          {title}
          {isActive ? (
            sortConfig.direction === "asc" ? (
              <ArrowUp className="size-3 text-primary" />
            ) : (
              <ArrowDown className="size-3 text-primary" />
            )
          ) : (
            <ArrowUpDown className="size-3 opacity-20" />
          )}
        </div>
      </Table.Head>
    );
  };

  return (
    <div className={"statsContainer flex flex-col gap-8"}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold text-slate-900">Performance Summary</h2>
          <p className="text-sm text-slate-500">Totals for every post in this {type.toLowerCase()} that went live.</p>
        </div>
        <Button
          onClick={() => exportStats()}
          disabled={loading || !stats}
          loading={loading}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <FileDown className="size-4" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Impressions"
          total={stats?.totals?.impressionCount}
          average={stats?.averages?.impressionCount}
          reportingCount={stats?.reportingCounts?.impressionCount}
          icon={<Eye className="size-4" />}
        />
        <StatCard
          title="Reactions"
          total={stats?.totals?.totalReactions}
          average={stats?.averages?.totalReactions}
          reportingCount={stats?.reportingCounts?.totalReactions}
          icon={<Heart className="size-4" />}
        />
        <StatCard
          title="Comments"
          total={stats?.totals?.commentCount}
          average={stats?.averages?.commentCount}
          reportingCount={stats?.reportingCounts?.commentCount}
          icon={<MessageCircle className="size-4" />}
        />
        <StatCard
          title="Shares"
          total={stats?.totals?.shareCount}
          average={stats?.averages?.shareCount}
          reportingCount={stats?.reportingCounts?.shareCount}
          icon={<Share2 className="size-4" />}
        />
        {smartLinkClicks !== undefined && (
          <StatCard
            title="Smart Link Clicks"
            total={smartLinkClicks}
            onShowMore={showSmartLinkStats}
            icon={<Share2 className="size-4" />}
          />
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-slate-900">Post Breakdown</h3>
            <Badge
              variant="outline"
              className="rounded-full px-2.5 py-0.5 bg-slate-50 font-bold text-slate-600 border-slate-200"
            >
              {stats?.breakdown?.length || 0} Posts
            </Badge>
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
          <Table>
            <Table.Header className="bg-slate-50/50">
              <Table.Row className="hover:bg-transparent">
                <SortableHeader column="playerName" title="Player" />
                {type == "Campaign" && <SortableHeader column="promptTitle" title="Prompt" />}
                <SortableHeader column="socialNetworkId" title="Network" align="center" />
                <SortableHeader column="postedTimestamp" title="Posted" />
                <SortableHeader column="latestStats.impressionCount" title="Impressions" align="center" />
                <SortableHeader column="latestStats.totalReactions" title="Reactions" align="center" />
                <SortableHeader column="latestStats.commentCount" title="Comments" align="center" />
                <SortableHeader column="latestStats.shareCount" title="Shares" align="center" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedBreakdown.length > 0 ? (
                sortedBreakdown.map(statEntry => {
                  const [date, ...time] = printDateWithTimeZone(statEntry.postedTimestamp).split(" ");
                  // Only rows that name a post have something to open.
                  const clickable = !!onRowClick && statEntry.postId != null;
                  return (
                    <Table.Row
                      key={`stats-breakdown-${statEntry.postSocialNetworkId}`}
                      className={cn("group hover:bg-slate-50/50", clickable && "cursor-pointer hover:bg-slate-50")}
                      {...(clickable && onRowClick
                        ? {
                            role: "button",
                            tabIndex: 0,
                            onClick: () => onRowClick(statEntry),
                            onKeyDown: (e: React.KeyboardEvent) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onRowClick(statEntry);
                              }
                            },
                          }
                        : {})}
                    >
                      <Table.Cell>
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-900 text-sm">{statEntry.playerName}</span>
                          <a
                            href={`/coach-players/${statEntry.playerId}`}
                            onClick={e => e.stopPropagation()}
                            className="text-xs text-slate-500 hover:text-primary transition-colors font-medium"
                          >
                            {statEntry.playerEmail}
                          </a>
                        </div>
                      </Table.Cell>
                      {type == "Campaign" && (
                        <Table.Cell>
                          <a
                            href={URLS.coach.prompt.replace(":promptId", `${statEntry.promptId}`)}
                            className="text-sm font-medium text-slate-700 hover:text-primary transition-colors"
                          >
                            {statEntry.promptTitle}
                          </a>
                        </Table.Cell>
                      )}
                      <Table.Cell>
                        <div className="flex justify-center">
                          {wrapInLinkIfHrefIsPresent(
                            statEntry.externalPostUrl,
                            <div className="p-1.5 bg-slate-100 rounded-lg group-hover:bg-white transition-colors">
                              <SocialNetworkIcon socialNetworkId={statEntry.socialNetworkId} size="small" />
                            </div>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-slate-700">{date}</span>
                          <span className="text-xs text-slate-400 font-medium">{time.join(" ")}</span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-center font-bold text-slate-700">
                        {statEntry.latestStats?.impressionCount !== null &&
                        statEntry.latestStats?.impressionCount !== undefined
                          ? kFormatter(statEntry.latestStats.impressionCount)
                          : "--"}
                      </Table.Cell>
                      <Table.Cell className="text-center font-bold text-slate-700">
                        {statEntry.latestStats?.totalReactions !== null &&
                        statEntry.latestStats?.totalReactions !== undefined
                          ? kFormatter(statEntry.latestStats.totalReactions)
                          : "--"}
                      </Table.Cell>
                      <Table.Cell className="text-center font-bold text-slate-700">
                        {statEntry.latestStats?.commentCount !== null &&
                        statEntry.latestStats?.commentCount !== undefined
                          ? kFormatter(statEntry.latestStats.commentCount)
                          : "--"}
                      </Table.Cell>
                      <Table.Cell className="text-center font-bold text-slate-700">
                        {statEntry.latestStats?.shareCount !== null && statEntry.latestStats?.shareCount !== undefined
                          ? kFormatter(statEntry.latestStats.shareCount)
                          : "--"}
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              ) : (
                <Table.Row>
                  <Table.Cell
                    colSpan={type === "Campaign" ? 8 : 7}
                    className="h-32 text-center text-slate-400 font-medium"
                  >
                    No posts found for this {type.toLowerCase()}.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default PostsStats;
